import dayjs from "dayjs";
import { FC, useEffect, useState } from "react";
import { useFormContext } from "react-hook-form";
import { useGetLoggedInUserDetails } from "../../../../../aAdminTabTeam2/hooksAndFunctions/customHooks/useGetLoggedInUserDetails";
import {
  FormController,
  MuiDateFieldFormController,
  MuiGrid,
  MuiSelect,
  MuiTextField,
  OptionsDataType,
  OptionsDataTypeWithNode,
} from "../../../../../components";
import { ClientAutoComplete } from "../../../../../components/clientAutoComplete/ClientAutoComplete";
import {
  MAX_LENGTH_100,
  MAX_LENGTH_32,
  PAGE_IDS,
} from "../../../../../helpers/constants";
import { getDateFormatByCountry } from "../../../../../helpers/constants/countryDateFormat";
import {
  getGenericAccountStatusFromExpirationDate,
  isNotEmpty,
  isPurposeClient,
  isStatusExpired,
  isTestAccess,
} from "../../../../../helpers/utils";
import { useNavigationSiteCore } from "../../../../../hooks/useNavigationSiteCore";
import { useSiteCore } from "../../../../../hooks/useSiteCore";
import { CreateGenericAccountFormKey } from "../../modelsAndSchemas/Models";
import { createGenericAccounts } from "../../siteCore/SiteCoreKeys";
import { CreateGenericAccountBasicDetailsFormWrapper } from "./CreateGenericAccountBasicDetailsForm.styles";

export const pageId = PAGE_IDS.CREATE_GENERIC_ACCOUNT;

interface CreateGenericAccountBasicDetailsFormProps {
  isEditGenericAccount?: boolean;
  usersList: OptionsDataType[];
}

const gridSize = { lg: 4, md: 6, xs: 12, sm: 6 };
export const CreateGenericAccountBasicDetailsForm: FC<
  CreateGenericAccountBasicDetailsFormProps
> = ({ isEditGenericAccount, usersList }) => {
  const { fetchLabel, commonLabels, fetchDropdownValues } = useSiteCore();
  const { commonARIALabel } = useNavigationSiteCore();
  const { loggedInUser } = useGetLoggedInUserDetails();

  const { control, getValues, setValue, clearErrors, formState } =
    useFormContext();

  const [clientDropdown, setClientDropdown] = useState<{
    showClientDropdown: boolean;
    isMandatory: boolean;
  }>({
    showClientDropdown: true,
    isMandatory: false,
  });

  const {
    createGenericAccountBasicDetailsTitle,
    genericAccountName,
    purpose,
    client,
    basicDetailsGenericRegistrationCode,
    startDate,
    expirationDate,
    basicDetailsNumberOfAccounts,
    programDurationDays,
    requestedBy,
    purposeList,
    createGenericAccountStatusList,
    createGenericAccountStatusLabel,
    createGenericAccountKeyDetailsTitle,
    editViewGenericAccountStatusLabel,
    editViewGenericAccountStatusList,
  } = createGenericAccounts;

  const shouldDisableStatus = isStatusExpired(
    getGenericAccountStatusFromExpirationDate(
      getValues(CreateGenericAccountFormKey.status),
      getValues(CreateGenericAccountFormKey.expirationDate),
    ),
  );

  const getLabel = (key: string) => {
    return fetchLabel(createGenericAccountBasicDetailsTitle, key);
  };

  const getStatusLabel = () => {
    return isEditGenericAccount
      ? fetchLabel(
          createGenericAccountKeyDetailsTitle,
          editViewGenericAccountStatusLabel,
        )
      : getLabel(createGenericAccountStatusLabel);
  };

  const getStatusDropdownList = () => {
    const [parentKey, statusListKey] =
      isEditGenericAccount && shouldDisableStatus
        ? [
            createGenericAccountKeyDetailsTitle,
            editViewGenericAccountStatusList,
          ]
        : [
            createGenericAccountBasicDetailsTitle,
            createGenericAccountStatusList,
          ];
    return fetchDropdownValues(parentKey, statusListKey);
  };

  const userCountryDateFormat = getDateFormatByCountry(
    loggedInUser?.countryCode ?? "",
  );

  const formatDateValue = (value?: string) => {
    return isNotEmpty(value) ? dayjs(value, userCountryDateFormat) : null;
  };

  const handlePurposeListChange = (value: string) => {
    setClientDropdown({
      showClientDropdown: !isTestAccess(value),
      isMandatory: isPurposeClient(value),
    });
    if (
      !isPurposeClient(value) &&
      isNotEmpty(formState.errors) &&
      formState.errors?.clientId
    ) {
      clearErrors(CreateGenericAccountFormKey.clientId);
    }
  };

  useEffect(() => {
    const { purpose } = getValues();
    handlePurposeListChange(purpose);
  }, [isEditGenericAccount, getValues(CreateGenericAccountFormKey.purpose)]);

  const handleEffectiveDateValidation = (newValue: dayjs.Dayjs | null) => {
    setValue(CreateGenericAccountFormKey.startDateWithoutFormatted, newValue);
  };
  const handleExpireDateValidation = (newValue: dayjs.Dayjs | null) => {
    setValue(
      CreateGenericAccountFormKey.expirationDateWithoutFormatted,
      newValue,
    );
  };

  return (
    <CreateGenericAccountBasicDetailsFormWrapper>
      <form>
        <MuiGrid container className="main-container">
          <MuiGrid size={gridSize} className="company-name form-field">
            <FormController
              control={control}
              name={CreateGenericAccountFormKey.genericAccountName}
            >
              <MuiTextField
                isMandatory
                id={`${pageId}-${CreateGenericAccountFormKey.genericAccountName}`}
                label={getLabel(genericAccountName)}
                showCloseIcon
                placeholder={commonLabels.inputPlaceHolder}
                disabled={isEditGenericAccount}
                maxLength={MAX_LENGTH_100}
                requiredARIALabel={commonARIALabel.required}
              />
            </FormController>
          </MuiGrid>

          <MuiGrid size={gridSize} className="form-field">
            <FormController
              control={control}
              name={CreateGenericAccountFormKey.purpose}
            >
              <MuiSelect
                id={`${pageId}-${CreateGenericAccountFormKey.purpose}`}
                label={getLabel(purpose)}
                visibleDefaultSelectOption
                isMandatory
                showSearch
                disabled={isEditGenericAccount}
                options={fetchDropdownValues(
                  createGenericAccountBasicDetailsTitle,
                  purposeList,
                )}
                placeholder={commonLabels.selectPlaceHolder}
                onChange={(event) => {
                  handlePurposeListChange(event.target.value as string);
                }}
                requiredARIALabel={commonARIALabel.required}
              />
            </FormController>
          </MuiGrid>

          {clientDropdown.showClientDropdown && (
            <MuiGrid size={gridSize} className="form-field">
              {isEditGenericAccount ? (
                <FormController
                  control={control}
                  name={CreateGenericAccountFormKey.clientName}
                >
                  <MuiTextField
                    label={getLabel(client)}
                    placeholder={commonLabels.inputPlaceHolder}
                    id={`${pageId}-${CreateGenericAccountFormKey.clientName}`}
                    disabled={isEditGenericAccount}
                    requiredARIALabel={commonARIALabel.required}
                  />
                </FormController>
              ) : (
                <ClientAutoComplete
                  isMandatory={clientDropdown.isMandatory}
                  label={getLabel(client)}
                  formId={CreateGenericAccountFormKey.clientId}
                  control={control}
                  isActiveClientOnly
                />
              )}
            </MuiGrid>
          )}

          <MuiGrid size={gridSize} className="form-field">
            <FormController
              control={control}
              name={CreateGenericAccountFormKey.genericRegistrationCode}
            >
              <MuiTextField
                isMandatory
                showCloseIcon
                id={`${pageId}-${CreateGenericAccountFormKey.genericRegistrationCode}`}
                label={getLabel(basicDetailsGenericRegistrationCode)}
                placeholder={commonLabels.inputPlaceHolder}
                disabled={isEditGenericAccount}
                maxLength={MAX_LENGTH_32}
                requiredARIALabel={commonARIALabel.required}
              />
            </FormController>
          </MuiGrid>

          <MuiGrid size={gridSize} className="form-field">
            <MuiDateFieldFormController
              control={control}
              isMandatory
              name={CreateGenericAccountFormKey.startDate}
              id={`${pageId}-${CreateGenericAccountFormKey.startDate}`}
              value={formatDateValue(
                getValues(CreateGenericAccountFormKey.startDate),
              )}
              label={getLabel(startDate)}
              format={userCountryDateFormat}
              valueFormat={userCountryDateFormat}
              disabled={isEditGenericAccount}
              disableLabel={isEditGenericAccount}
              disablePast
              disableManualInput
              onChange={(newValue) => {
                setValue(CreateGenericAccountFormKey.startDate, newValue);
                handleEffectiveDateValidation(newValue);
              }}
              requiredARIALabel={commonARIALabel.required}
              selectDateARIALabel={commonARIALabel.selectDate}
            />
          </MuiGrid>
          <MuiGrid size={gridSize} className="form-field">
            <MuiDateFieldFormController
              control={control}
              isMandatory
              name={CreateGenericAccountFormKey.expirationDate}
              id={`${pageId}-${CreateGenericAccountFormKey.expirationDate}`}
              value={formatDateValue(
                getValues(CreateGenericAccountFormKey.expirationDate),
              )}
              format={userCountryDateFormat}
              label={getLabel(expirationDate)}
              valueFormat={userCountryDateFormat}
              disableManualInput
              disablePast
              onChange={(newValue) => {
                setValue(CreateGenericAccountFormKey.expirationDate, newValue);
                handleExpireDateValidation(newValue);
              }}
              requiredARIALabel={commonARIALabel.required}
              selectDateARIALabel={commonARIALabel.selectDate}
            />
          </MuiGrid>

          <MuiGrid size={gridSize} className="form-field">
            <FormController
              control={control}
              name={CreateGenericAccountFormKey.status}
            >
              <MuiSelect
                id={`${pageId}-${CreateGenericAccountFormKey.status}`}
                label={getStatusLabel()}
                options={getStatusDropdownList()}
                disabled={isEditGenericAccount && shouldDisableStatus}
                visibleDefaultSelectOption={false}
                requiredARIALabel={commonARIALabel.required}
                placeholder={commonLabels.selectPlaceHolder}
              />
            </FormController>
          </MuiGrid>

          <MuiGrid size={gridSize} className="form-field">
            <FormController
              control={control}
              name={CreateGenericAccountFormKey.numberOfAccounts}
            >
              <MuiTextField
                id={`${pageId}-${CreateGenericAccountFormKey.numberOfAccounts}`}
                showCloseIcon
                isMandatory
                label={getLabel(basicDetailsNumberOfAccounts)}
                placeholder={commonLabels.inputPlaceHolder}
                type="number"
                inputNumberWithoutDecimal
                requiredARIALabel={commonARIALabel.required}
              />
            </FormController>
          </MuiGrid>

          <MuiGrid size={gridSize} className="form-field">
            <FormController
              control={control}
              name={CreateGenericAccountFormKey.programDuration}
            >
              <MuiTextField
                id={`${pageId}-${CreateGenericAccountFormKey.programDuration}`}
                label={getLabel(programDurationDays)}
                isMandatory
                placeholder={commonLabels.inputPlaceHolder}
                showCloseIcon
                type="number"
                inputNumberWithoutDecimal
                requiredARIALabel={commonARIALabel.required}
              />
            </FormController>
          </MuiGrid>

          <MuiGrid size={gridSize} className="form-field">
            <FormController
              control={control}
              name={CreateGenericAccountFormKey.requestedBy}
            >
              <MuiSelect
                id={`${pageId}-${CreateGenericAccountFormKey.requestedBy}`}
                label={getLabel(requestedBy)}
                options={usersList}
                disabled={isEditGenericAccount}
                placeholder={commonLabels.selectPlaceHolder}
                searchPlaceholder={commonLabels.search}
                showSearch
                getSubText={(
                  option: OptionsDataType | OptionsDataTypeWithNode,
                ) => option.email}
                requiredARIALabel={commonARIALabel.required}
              />
            </FormController>
          </MuiGrid>
        </MuiGrid>
      </form>
    </CreateGenericAccountBasicDetailsFormWrapper>
  );
};
